import type { StaffRole } from '@/api/types';

/**
 * Where a signed-in staff member lands. A navigation hint only: it grants nothing, and no API call
 * depends on it. Authorization is the backend's.
 */
export function roleHome(role: StaffRole): string {
  switch (role) {
    case 'ROLE_WAITER':
      return '/staff';
    case 'ROLE_KITCHEN':
      return '/kitchen';
    case 'ROLE_CASHIER':
      return '/counter';
    case 'ROLE_SUPER_ADMIN':
      return '/admin';
    case 'ROLE_BRANCH_MANAGER':
    case 'ROLE_BRAND_OWNER':
      return '/manage';
  }
}

/**
 * Roles the backend allows on the kitchen endpoints (`GET /kitchen/tickets`, availability, reprint).
 * A hint for what to show: the backend still decides every request.
 */
export function canUseKitchen(role: StaffRole): boolean {
  return role === 'ROLE_KITCHEN' || role === 'ROLE_BRANCH_MANAGER';
}

/** Only a branch manager may void an order line (`POST /kitchen/items/{id}/void`). A hint, not security. */
export function canVoidOrderLines(role: StaffRole): boolean {
  return role === 'ROLE_BRANCH_MANAGER';
}

/**
 * Roles the backend allows on the counter endpoints (`/counter/*`): cashier and branch manager. A hint for
 * what to show; the backend still decides every request.
 */
export function canUseCounter(role: StaffRole): boolean {
  return role === 'ROLE_CASHIER' || role === 'ROLE_BRANCH_MANAGER';
}

/** Only a branch manager may adjust a bill (`POST /counter/bills/{id}/adjustments`). A hint, not security. */
export function canAdjustBills(role: StaffRole): boolean {
  return role === 'ROLE_BRANCH_MANAGER';
}

/**
 * Only a waiter or a branch manager may open a session (`POST /staff/sessions`), and only a manager may
 * force-close one. Hints for what to show, not security.
 */
export function canOpenSessions(role: StaffRole): boolean {
  return role === 'ROLE_BRANCH_MANAGER';
}

export function canForceClose(role: StaffRole): boolean {
  return role === 'ROLE_BRANCH_MANAGER';
}

/**
 * Roles the backend allows on the waiter endpoints (`/staff/menu`, `/staff/active-sessions`,
 * `/staff/sessions`, cart additions, opening sessions, transfers): waiter and branch manager. A hint for
 * what to show; the backend still decides every request. A manager can also open a session, and a cashier
 * can read the floor but has no waiter screen.
 */
export function canUseWaiterSurface(role: StaffRole): boolean {
  return role === 'ROLE_WAITER' || role === 'ROLE_BRANCH_MANAGER';
}

/**
 * Roles the backend allows on the endpoints the management screens use (`GET /staff/floor`, `GET /staff/menu`,
 * `POST /kitchen/items/{id}/availability`): the branch manager. A brand owner is sent to `/manage` by
 * `roleHome` but no endpoint accepts that role yet, so it is not listed. A hint for what to show; the
 * backend still decides every request.
 */
export function canUseManagement(role: StaffRole): boolean {
  return role === 'ROLE_BRANCH_MANAGER';
}

/** Platform administration is for the super admin only. A hint for what to show, not security. */
/**
 * Roles the backend's event stream serves (kitchen, waiter, cashier, branch manager). The platform admin and
 * the brand owner have no live screen, so the app never opens the stream for them and never shows a
 * "live updates" banner they cannot act on.
 */
export function canUseLiveUpdates(role: StaffRole): boolean {
  return (
    role === 'ROLE_KITCHEN' ||
    role === 'ROLE_WAITER' ||
    role === 'ROLE_CASHIER' ||
    role === 'ROLE_BRANCH_MANAGER'
  );
}

export function canUseAdmin(role: StaffRole): boolean {
  return role === 'ROLE_SUPER_ADMIN';
}
